import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ManageAccounts,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { destroySessionManager } from '../utils/sessionManager';
const drawerWidth = 280;

interface LayoutProps {
  onLogout: () => void;
}

const Layout: React.FC<LayoutProps> = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Get user role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'SUPER_ADMIN';

  // Dynamic nav items based on role
  const navItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Clients', icon: <PeopleIcon />, path: '/clients' },
    { text: 'Filings', icon: <AssignmentIcon />, path: '/filings' },
    { text: 'Reports', icon: <DescriptionIcon />, path: '/reports' },
    ...(isSuperAdmin ? [
      { text: 'Super Admin', icon: <SettingsIcon />, path: '/super-admin' },
      { text: 'User Management', icon: <ManageAccounts />, path: '/users' }
    ] : []),
  ];

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-20%',
          right: '-20%',
          width: '60%',
          height: '60%',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />

      <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: 0,
            }}
          >
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: '#FFFFFF' }}>H</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#FFFFFF' }}>
              HST Tracker
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8125rem', fontWeight: 500 }}>
              Tax Management
            </Typography>
          </Box>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 3, py: 2, position: 'relative', zIndex: 1 }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.text}
              onClick={() => { navigate(item.path); isMobile && setMobileOpen(false); }}
              sx={{
                mb: 1,
                py: 1.75,
                px: 2.5,
                backgroundColor: isActive ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                backdropFilter: isActive ? 'blur(10px)' : 'none',
                border: isActive ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid transparent',
                color: '#FFFFFF',
                borderRadius: 0,
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)', transform: 'translateX(4px)' },
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{ color: '#FFFFFF', minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{ fontSize: '0.9375rem', fontWeight: isActive ? 600 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ p: 3, position: 'relative', zIndex: 1 }}>
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            cursor: 'pointer',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 0,
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.15)' },
            transition: 'all 0.2s ease',
          }}
        >
          <Avatar sx={{ width: 40, height: 40, backgroundColor: '#FFFFFF', color: '#667eea', fontWeight: 700, borderRadius: 0 }}>
            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: '#FFFFFF' }}>
              {user.name || 'User'}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
              {user.role === 'SUPER_ADMIN' ? 'Super Admin' : user.role === 'ADMIN' ? 'Admin' : 'Employee'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          ml: { md: desktopOpen ? `${drawerWidth}px` : 0 },
          transition: 'all 0.3s ease',
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #E5E7EB',
        }}
      >
        <Toolbar sx={{ minHeight: '72px !important' }}>
              <IconButton 
                onClick={() => isMobile ? setMobileOpen(!mobileOpen) : setDesktopOpen(!desktopOpen)} 
                sx={{ mr: 2, color: '#1a1a1a' }}
              >
              <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: desktopOpen ? drawerWidth : 0 }, flexShrink: { md: 0 }, transition: 'width 0.3s ease' }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              border: 'none',
              borderRadius: 0,
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: desktopOpen ? drawerWidth : 0,
              transition: 'width 0.3s ease',
              overflow: 'hidden',
              border: 'none',
              borderRadius: 0,
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ sx: { mt: -1, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', borderRadius: 0 } }}
      >
        <MenuItem 
          onClick={() => { 
            console.log('👋 User logged out manually');
            setAnchorEl(null); 
            destroySessionManager(); // Stop session monitoring
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            onLogout(); 
            navigate('/login'); 
          }} 
          sx={{ py: 1.5, color: '#EF4444' }}
        >
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#EF4444' }} /></ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

        <Box component="main" sx={{ flexGrow: 1, width: { md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' }, minHeight: '100vh', backgroundColor: '#F8F9FA', transition: 'width 0.3s ease' }}>
          <Toolbar sx={{ minHeight: '72px !important' }} />
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;